/* ============================================================
   FIND IN STORE — per-shop availability for the selected variant

   Read-only. Fetches availability from the app proxy the first time the
   drawer is opened for a given variant, caches it per variant for the
   life of the page, and re-fetches when the customer changes variant.
   Nothing here writes stock.

   The state shown is decided here, not by the API. The API reports a
   stock level per shop; whether holding stock means "buy and take away"
   or "view only" comes from the product's membership of the view-only
   collection, passed in via data-view-only.

   Four states, shown as a small coloured dot with its wording beside it:
     in-stock   green   held and available to buy
     low-stock  amber   held, but at or under the low threshold
     view-only  neutral a display piece — level does not apply
     no-stock   red     not held at that shop

   When the endpoint returns a quantity and data-show-quantity is on, the
   count is appended to the wording. Quantities are never shown for a display
   piece or for a shop with none: "how many" is not the question in the first
   case and the answer is zero in the second.

   Opening and closing belong to the theme's <side-drawer> (drawer.js),
   which also handles Escape, click-outside and focus trapping. This
   component only needs to know when the drawer became visible, which it
   watches for rather than hooking the toggle — so it stays correct
   however the drawer was opened.

   Usage is published as three Shopify custom events — opened, searched,
   shop clicked — which the GA4 custom pixel (pixels/ga4-supplementary.js
   in the Claude-Shopify-SWD repo) forwards to GA4. Nothing is sent from
   here directly, so cookie consent is the pixel's to enforce. A searched
   postcode is reported as its district only ("SP1"), never in full.
   ============================================================ */

const FIS_STATE = {
  IN_STOCK: "in-stock",
  LOW_STOCK: "low-stock",
  VIEW_ONLY: "view-only",
  NO_STOCK: "no-stock",
};

class FindInStore extends HTMLElement {
  constructor() {
    super();

    this.endpoint = this.dataset.endpoint;
    this.viewOnly = this.dataset.viewOnly === "true";
    this.useSampleData = this.dataset.sampleData === "true";
    this.showQuantity = this.dataset.showQuantity === "true";
    this.lowThreshold = parseInt(this.dataset.lowThreshold, 10) || 2;
    this.variantId = this.dataset.variantId;
    this.productId = this.dataset.productId;
    this.productTitle = this.dataset.productTitle;

    this.postcodeSearch = this.dataset.postcodeSearch === "true";
    this.dataUrl = this.dataset.postcodeData;
    this.unit = this.dataset.distanceUnit === "km" ? "km" : "miles";
    this.geoData = null;
    this.geoPromise = null;

    this.drawer = this.closest("side-drawer");
    this.list = this.querySelector(".find-in-store__list");
    this.status = this.querySelector(".find-in-store__status");
    this.search = this.querySelector(".find-in-store__search-input");
    this.originNote = this.querySelector(".find-in-store__origin");

    // Responses keyed by variant id, so switching back and forth between
    // sizes doesn't re-request.
    this.cache = new Map();
    this.stores = this.readStoreList();
    this.query = "";
    this.loaded = false;
    // Set once a postcode or town has been resolved: {label, lat, lng}. While
    // set, the list is ordered by distance instead of alphabetically.
    this.origin = null;
    this.geocoding = false;
    this.lookupTimer = null;
    // Opening the drawer changes its class more than once, so the observer
    // below can fire several times for a single open. Without this guard
    // each of those would start its own request.
    this.loading = false;
    // Tracking state. The observer fires several times per open, so an open
    // is only counted on the closed -> open transition. A search is reported
    // once the customer stops typing, and not again if nothing changed.
    this.wasOpen = false;
    this.searchReportTimer = null;
    this.lastSearchReport = "";

    this.addEventListeners();
  }

  readStoreList() {
    const el = this.querySelector(".find-in-store__stores");
    if (!el) return [];
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return [];
    }
  }

  get isOpen() {
    return Boolean(this.drawer && this.drawer.classList.contains("active"));
  }

  addEventListeners() {
    // Load on first open rather than on page load — most visitors never
    // open the drawer, and this keeps it off the critical path. Watching
    // the class rather than the toggle keeps this correct no matter how
    // the drawer came to be open.
    if (this.drawer) {
      this.observer = new MutationObserver(() => {
        const open = this.isOpen;
        if (open && !this.wasOpen) this.track("swd_find_in_store_opened");
        this.wasOpen = open;
        if (open) this.onOpen();
      });
      this.observer.observe(this.drawer, {
        attributes: true,
        attributeFilter: ["class"],
      });
    }

    if (this.search) {
      this.search.addEventListener("input", () => {
        this.query = this.search.value.trim().toLowerCase();
        window.clearTimeout(this.lookupTimer);
        window.clearTimeout(this.searchReportTimer);
        this.searchReportTimer = window.setTimeout(() => this.reportSearch(), 1500);

        // Clearing the box drops back to the plain alphabetical list.
        if (!this.query) {
          this.clearOrigin();
          this.render();
          return;
        }

        /* A postcode looks up on its own after a short pause. The field invites
           one, and a postcode matches no shop name, so text-filtering it would
           dead-end on "no shop matches that search" — which is what a customer
           typing their postcode used to get. The pause is what stops every
           keystroke of a half-typed postcode firing a request. Town names still
           filter instantly and need no lookup at all. */
        if (this.isLocationQuery()) {
          // Resolution is local, so this only waits long enough to stop the
          // order shuffling on every keystroke of a part-typed postcode.
          this.lookupTimer = window.setTimeout(() => this.runLocationSearch(), 150);
        } else {
          // Switching from a postcode back to a name means they want the list
          // by name again, not by distance.
          if (this.origin) this.clearOrigin();

          /* A town we have no shop in — "Bath", "Guildford" — matches nothing
             by name. Rather than a dead end, look it up and show the nearest
             shops, which is what the customer actually wanted to know. */
          if (!this.hasTextMatch()) {
            this.lookupTimer = window.setTimeout(() => this.runLocationSearch(), 150);
          }
        }

        this.render();
      });

      // There is no search button — results update as they type. Enter just
      // skips the remaining pause for anyone who expects to press it.
      this.search.addEventListener("keydown", (event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        window.clearTimeout(this.lookupTimer);
        this.runLocationSearch();
        // Pressing Enter says they have finished typing, so report sooner.
        window.clearTimeout(this.searchReportTimer);
        this.searchReportTimer = window.setTimeout(() => this.reportSearch(), 500);
      });
    }

    // One listener for the whole list, because the rows are rebuilt on every
    // render. Only fires for the shop page link and the phone number.
    if (this.list) {
      this.list.addEventListener("click", (event) => {
        const link = event.target.closest("a.find-in-store__store-name, a.find-in-store__phone");
        if (!link) return;
        const row = link.closest(".find-in-store__store");
        const name = row && row.querySelector(".find-in-store__store-name");
        const state = row && row.querySelector(".find-in-store__state");
        const stateMatch = state && state.className.match(/find-in-store__state--([\w-]+)/);
        this.track("swd_find_in_store_shop_clicked", {
          shopName: name ? name.textContent : "",
          linkType: link.classList.contains("find-in-store__phone") ? "phone" : "shop_page",
          stockState: stateMatch ? stateMatch[1] : "",
          searched: Boolean(this.origin),
        });
      });
    }

    const sizeSelect = this.querySelector(".find-in-store__size-select");
    if (sizeSelect) {
      sizeSelect.addEventListener("change", () => this.selectVariant(sizeSelect.value));
    }

    // The theme announces a variant change by setting the hidden
    // input[name="id"] and dispatching a bubbling change event on it.
    document.querySelectorAll('product-form input[name="id"]').forEach((input) => {
      input.addEventListener("change", () => {
        const next = String(input.value || "");
        if (!next || next === this.variantId) return;
        this.variantId = next;
        this.loaded = false;
        this.syncVariantChips();
        if (this.isOpen) this.onOpen();
      });
    });
  }

  /* Drive the page's own selector rather than duplicating variant logic: set
     each option <select> to this variant's values and fire change, and the
     theme does the rest — including updating the hidden input this component
     already watches. */
  selectVariant(id) {
    const vs = document.querySelector("variant-selects");
    if (!vs) return;
    const json = vs.querySelector('script[type="application/json"]');
    if (!json) return;

    let variants;
    try {
      variants = JSON.parse(json.textContent);
    } catch (e) {
      return;
    }

    const wanted = variants.find((v) => String(v.id) === String(id));
    if (!wanted) return;

    const selects = [...vs.querySelectorAll("select")];
    if (!selects.length) return;
    (wanted.options || []).forEach((value, i) => {
      if (selects[i]) selects[i].value = value;
    });
    selects[selects.length - 1].dispatchEvent(new Event("change", { bubbles: true }));
  }

  // Keep the picker in step when the size is changed on the page instead.
  syncVariantChips() {
    const sizeSelect = this.querySelector(".find-in-store__size-select");
    if (sizeSelect && sizeSelect.value !== String(this.variantId)) {
      sizeSelect.value = String(this.variantId);
    }
  }

  onOpen() {
    // Warm the postcode data alongside the availability request, so the first
    // postcode typed reorders the list without waiting on a download.
    if (this.postcodeSearch) this.ensureGeoData().catch(() => {});

    if (this.loaded) {
      this.render();
      return;
    }
    this.load();
  }

  async load() {
    if (this.loading) return;

    if (this.cache.has(this.variantId)) {
      this.loaded = true;
      this.render();
      return;
    }

    this.loading = true;
    this.setStatus(window.findInStoreStrings.loading);
    this.list.innerHTML = "";

    const requested = this.variantId;

    try {
      const data = this.useSampleData
        ? this.sampleResponse()
        : await this.fetchAvailability();
      this.cache.set(requested, data);
      // The customer may have changed variant while this was in flight —
      // don't paint a stale answer over the newer one.
      if (requested !== this.variantId) return;
      this.loaded = true;
      this.setStatus("");
      this.render();
    } catch (error) {
      if (requested !== this.variantId) return;
      this.setStatus("");
      this.list.innerHTML = "";
      this.list.appendChild(this.messageItem(window.findInStoreStrings.error));
    } finally {
      this.loading = false;
      // If the variant changed while that request was in flight, its own
      // load() call was turned away by the guard above — pick it up now.
      if (requested !== this.variantId && this.isOpen) this.load();
    }
  }

  async fetchAvailability() {
    const url = `${this.endpoint}?variantId=${encodeURIComponent(this.variantId)}`;
    const response = await fetch(url, { headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error(`Availability request failed: ${response.status}`);
    const data = await response.json();
    if (!data || !Array.isArray(data.stores)) throw new Error("Unexpected response shape");
    return data;
  }

  /* Stand-in for the endpoint while it is being built. Availability is
     derived from the variant id so a given product always shows the same
     pattern, and so reviewers see a realistic mix of all three states.
     Never reaches a customer: it is behind a section setting and the
     drawer shows a sample-data warning whenever it is on. */
  sampleResponse() {
    const seed = String(this.variantId)
      .split("")
      .reduce((total, char) => total + char.charCodeAt(0), 0);

    return {
      variantId: this.variantId,
      updatedAt: null,
      stores: this.stores.map((store, index) => {
        const n = (seed + index * 7) % 10;
        // 0 for none, 1-2 for low, 3-12 otherwise — the shape real shop
        // quantities take, so the layout is judged against realistic numbers.
        const quantity = n > 5 ? ((seed + index) % 10) + 3 : n > 3 ? ((seed + index) % 2) + 1 : 0;
        return { ...store, quantity };
      }),
    };
  }

  /* The endpoint returns name/url/address/lat/lng/quantity but no phone, so
     the number comes from the list held in the snippet, matched on name. If
     the endpoint ever returns one, it wins. */
  withDetails(store) {
    const known = this.stores.find((s) => s.name === store.name);
    return known ? { ...known, ...store, phone: store.phone || known.phone } : store;
  }

  render() {
    const data = this.cache.get(this.variantId);
    if (!data) return;

    let stores = data.stores.map((store) => this.withDetails(store))
      .filter((store) => this.matchesQuery(store));

    if (this.origin) {
      stores = stores
        .map((store) => ({ store, distance: this.distanceFrom(store) }))
        .sort((a, b) => {
          // A shop with no coordinates can't be placed — keep it, but last,
          // rather than dropping it or sorting it to the top as zero.
          if (a.distance === null) return 1;
          if (b.distance === null) return -1;
          return a.distance - b.distance;
        })
        .map((entry) => ({ ...entry.store, _distance: entry.distance }));
    }

    this.list.innerHTML = "";

    if (!stores.length) {
      this.list.appendChild(this.messageItem(window.findInStoreStrings.noMatches));
      return;
    }

    const fragment = document.createDocumentFragment();
    stores.forEach((store) => fragment.appendChild(this.storeItem(store)));
    this.list.appendChild(fragment);
  }


  /* ---------- postcode / town search ---------- */

  /* Resolution and distance both come from assets/uk-postcode-lookup.js, which
     the store locator on the Our Stores page uses too — so the two surfaces can
     never disagree about where a postcode is or how far a shop is from it.
     Everything happens locally; see that file for the data and its accuracy. */

  get lookup() {
    return window.SWDPostcodeLookup;
  }

  ensureGeoData() {
    if (!this.lookup) return Promise.reject(new Error("Postcode lookup not loaded"));
    return this.lookup.load(this.dataUrl);
  }

  classifyQuery(raw) {
    return this.lookup ? this.lookup.classify(raw) : null;
  }

  async runLocationSearch() {
    if (!this.postcodeSearch || this.geocoding) return;

    const parsed = this.classifyQuery(this.search ? this.search.value : "");
    if (!parsed) {
      this.clearOrigin();
      this.render();
      return;
    }

    this.geocoding = true;
    try {
      await this.ensureGeoData();
    } catch (error) {
      this.setStatus(window.findInStoreStrings.notFound);
      this.geocoding = false;
      return;
    }

    const origin = this.lookup.resolve(this.search.value);
    this.geocoding = false;

    if (!origin) {
      // Only say so for something meant to be a location. A plain name that
      // matches no town is just a filter that found nothing.
      this.origin = null;
      this.setOriginNote();
      if (parsed.kind !== "place") {
        this.setStatus(window.findInStoreStrings.notFound);
        // Leave the full list usable beneath the explanation rather than
        // filtering it to nothing with a postcode that matches no shop name.
        this.query = "";
      }
      this.render();
      return;
    }

    this.origin = origin;
    this.setStatus("");
    this.setOriginNote();
    // Distance ordering replaces text filtering — the customer asked "what is
    // near me", not "which names contain this".
    this.query = "";
    this.render();
  }

  clearOrigin() {
    this.origin = null;
    this.setStatus("");
    this.setOriginNote();
  }

  setOriginNote() {
    if (!this.originNote) return;
    if (!this.origin) {
      this.originNote.textContent = "";
      this.originNote.hidden = true;
      return;
    }
    this.originNote.textContent = (window.findInStoreStrings.nearest || "")
      .replace(/\{location\}/g, this.origin.label);
    this.originNote.hidden = false;
  }

  distanceFrom(store) {
    if (!this.origin || !this.lookup) return null;
    const lat = parseFloat(store.lat);
    const lng = parseFloat(store.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    const measure =
      this.unit === "km" ? this.lookup.distanceKm : this.lookup.distanceMiles;
    return measure(this.origin.lat, this.origin.lng, lat, lng);
  }

  formatDistance(distance) {
    if (distance < 1) return window.findInStoreStrings.distanceUnderOne || "";
    return (window.findInStoreStrings.distance || "")
      .replace(/\{distance\}/g, Math.round(distance))
      .replace(/\{unit\}/g, window.findInStoreStrings[this.unit] || this.unit);
  }

  /* True when what has been typed is a place to find, not a name to match. */
  isLocationQuery() {
    if (!this.postcodeSearch) return false;
    const parsed = this.classifyQuery(this.search ? this.search.value : "");
    return Boolean(parsed) && parsed.kind !== "place";
  }

  outwardOf(compact) {
    return this.lookup ? this.lookup.outwardOf(compact) : compact;
  }

  formatPostcode(compact) {
    return this.lookup ? this.lookup.formatPostcode(compact) : compact;
  }

  // Whether the typed text matches any shop by name or address.
  hasTextMatch() {
    const data = this.cache.get(this.variantId);
    if (!data || !this.query) return true;
    return data.stores.some((store) =>
      `${store.name} ${store.address || ""}`.toLowerCase().includes(this.query));
  }

  matchesQuery(store) {
    if (!this.query) return true;
    // A postcode is a location, not a shop name — filtering the list by it
    // would hide everything. Ordering by distance is the answer instead.
    if (this.isLocationQuery()) return true;
    return `${store.name} ${store.address || ""}`.toLowerCase().includes(this.query);
  }

  /* Three ways the endpoint can answer, most specific first: a quantity, a
     level band, or the original yes/no boolean. Each degrades cleanly, so the
     endpoint can start simple and get more precise without a theme change.

     A display piece is not a stock level, so view-only overrides all of it:
     for those products "how many" is not the question. */
  quantityFor(store) {
    const q = store.quantity;
    return Number.isFinite(q) ? q : null;
  }

  stateFor(store) {
    const quantity = this.quantityFor(store);

    let level = store.level;
    if (!level && quantity !== null) {
      level = quantity <= 0 ? "none" : quantity <= this.lowThreshold ? "low" : "in_stock";
    }
    if (!level) level = store.available ? "in_stock" : "none";

    if (level === "none") return FIS_STATE.NO_STOCK;
    if (this.viewOnly) return FIS_STATE.VIEW_ONLY;
    return level === "low" ? FIS_STATE.LOW_STOCK : FIS_STATE.IN_STOCK;
  }

  labelFor(state, store) {
    const strings = window.findInStoreStrings;

    if (state === FIS_STATE.VIEW_ONLY) return strings.viewOnly;
    if (state === FIS_STATE.NO_STOCK) return strings.noStock;

    const base = state === FIS_STATE.LOW_STOCK ? strings.lowStock : strings.inStock;
    const quantity = this.quantityFor(store);

    if (!this.showQuantity || quantity === null || quantity <= 0) return base;

    /* The phrasing is a format string per state, so low stock can carry more
       urgency than plain availability — "Low stock – only 2 available" against
       "In Stock – 7 available" — without either being hard-coded. {label} and
       {count} are the placeholders. Built as one string so a screen reader
       announces the whole phrase rather than three fragments. */
    const format =
      state === FIS_STATE.LOW_STOCK ? strings.formatLowStock : strings.formatInStock;

    if (!format) return base;

    return format.replace(/\{label\}/g, base).replace(/\{count\}/g, quantity);
  }

  storeItem(store) {
    const state = this.stateFor(store);

    const item = document.createElement("li");
    item.className = "find-in-store__store";

    // The shop name is the link to its own page, which carries the address,
    // opening hours and photographs — so none of that is repeated here.
    // Falls back to plain text if a shop has no page.
    const name = document.createElement(store.url ? "a" : "p");
    name.className = "find-in-store__store-name";
    name.textContent = store.name;
    if (store.url) name.href = store.url;
    item.appendChild(name);

    const wrapper = document.createElement("div");
    wrapper.className = `find-in-store__state find-in-store__state--${state}`;

    const dot = document.createElement("span");
    dot.className = "find-in-store__state-dot";
    dot.setAttribute("aria-hidden", "true");
    wrapper.appendChild(dot);

    // The wording stays alongside the dot on purpose. Colour on its own
    // carries no meaning for a colour-blind customer, and red/green is the
    // commonest form of that.
    const label = document.createElement("span");
    label.textContent = this.labelFor(state, store);
    wrapper.appendChild(label);

    item.appendChild(wrapper);

    /* Beneath the state rather than beside it. Costs a line per row, so fewer
       shops sit above the fold — accepted deliberately, because the number
       reads as belonging to the shop rather than floating at the right edge.
       Shown on every shop regardless of stock: someone whose nearest shop has
       none still wants to ring it, to ask when it lands or whether another
       branch will hold one. */
    if (store.phone) {
      const phone = document.createElement("a");
      phone.className = "find-in-store__phone";
      phone.href = `tel:+44${store.phone.replace(/\s+/g, "").replace(/^0/, "")}`;
      phone.textContent = store.phone;
      phone.setAttribute("aria-label", `Call ${store.name} on ${store.phone}`);
      item.appendChild(phone);
    }

    if (this.origin && Number.isFinite(store._distance)) {
      const distance = document.createElement("p");
      distance.className = "find-in-store__distance";
      distance.textContent = this.formatDistance(store._distance);
      item.appendChild(distance);
    }

    return item;
  }

  messageItem(text) {
    const item = document.createElement("li");
    item.className = "find-in-store__empty";
    item.textContent = text;
    return item;
  }

  setStatus(text) {
    if (this.status) this.status.textContent = text;
  }


  /* ---------- usage tracking ---------- */

  /* Hand the event to Shopify's customer events, where the GA4 custom pixel
     picks it up. Every event carries the product and the variant being looked
     at. Tracking must never break the drawer, so any failure is swallowed. */
  track(name, data) {
    try {
      const analytics = window.Shopify && window.Shopify.analytics;
      if (!analytics || typeof analytics.publish !== "function") return;
      analytics.publish(name, {
        productId: this.productId,
        productTitle: this.productTitle,
        variantId: this.variantId,
        viewOnly: this.viewOnly,
        ...data,
      });
    } catch (e) {
      // Never let analytics break the storefront.
    }
  }

  /* What the customer searched for, once they have stopped typing. Only a
     place we recognise is ever reported by name — a postcode as its district,
     a town as the town — because anything else typed into the box could be
     anything at all, including a full address. */
  reportSearch() {
    if (!this.search || !this.search.value.trim()) return;

    let report;
    if (this.origin) {
      const postcode = this.origin.kind === "postcode";
      report = {
        searchType: postcode ? "postcode" : "place",
        searchArea: postcode ? this.origin.label.split(" ")[0] : this.origin.label,
        found: true,
      };
    } else if (this.isLocationQuery()) {
      report = { searchType: "postcode", searchArea: "", found: false };
    } else {
      // Filtering by shop name. The text is reported only when it matched a
      // shop, so it is always part of a shop's own name or address. No match
      // means a town that could not be placed, reported without the text.
      report = this.hasTextMatch()
        ? { searchType: "shop_name", searchArea: this.query, found: true }
        : { searchType: "place", searchArea: "", found: false };
    }

    const key = `${report.searchType}|${report.searchArea}|${report.found}`;
    if (key === this.lastSearchReport) return;
    this.lastSearchReport = key;
    this.track("swd_find_in_store_searched", report);
  }
}

customElements.define("find-in-store", FindInStore);
